import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Zap, Check, Edit3, RefreshCw, Play, Pause, Loader2,
  Mail, Clock, ChevronDown, ChevronUp, Users, AlertTriangle,
  RotateCcw, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin/marketing';

const STEP_LABELS = {
  1: { label: 'Benvenuto', color: '#c9a84c' },
  2: { label: 'Il problema', color: '#7c3aed' },
  3: { label: 'Social proof', color: '#0891b2' },
  4: { label: 'Smart Home', color: '#059669' },
  5: { label: 'Guest App', color: '#0d9488' },
  6: { label: 'Prenotazioni', color: '#2563eb' },
  7: { label: 'Tariffe', color: '#d97706' },
  8: { label: 'Check-in', color: '#7c3aed' },
  9: { label: 'Notifiche', color: '#0891b2' },
  10: { label: 'Admin Panel', color: '#6366f1' },
  11: { label: 'Integrazioni', color: '#059669' },
  12: { label: 'Sicurezza', color: '#dc2626' },
  13: { label: 'ROI', color: '#c9a84c' },
  14: { label: 'Case study', color: '#0d9488' },
  15: { label: 'Aggiornamenti', color: '#6d28d9' },
  16: { label: 'Setup', color: '#2563eb' },
  17: { label: 'Confronto', color: '#d97706' },
  18: { label: 'Testimonianza', color: '#dc2626' },
  19: { label: 'Piano Pro', color: '#c9a84c' },
  20: { label: 'Ultima chiamata', color: '#ef4444' },
};

const BATCH_LABELS = ['Email 1–5', 'Email 6–10', 'Email 11–15', 'Email 16–20'];

/** Restituisce il prossimo batch da generare (1-4) oppure null se completo */
function getNextBatch(seq) {
  if (!seq) return 1;
  const status = seq.batch_status;
  if (status === 'complete') return null;
  if (status === 'batch3_done') return 4;
  if (status === 'batch2_done') return 3;
  if (status === 'batch1_done') return 2;
  return 1; // nessun batch completato → ricomincia
}

export function LeadSequenceTab({ storedKey }) {
  const [sequence, setSequence] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateStep, setGenerateStep] = useState(0);
  const [toggling, setToggling] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');

  const headers = { 'X-Superadmin-Key': storedKey };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [seqRes, progRes] = await Promise.all([
        axios.get(`${API}/lead-sequence`, { headers }),
        axios.get(`${API}/lead-sequence/progress`, { headers }),
      ]);
      setSequence(seqRes.data.sequence);
      setProgress(progRes.data);
    } catch {
      toast.error('Errore nel caricamento della sequenza.');
    } finally {
      setLoading(false);
    }
  };

  /** Genera la sequenza completa partendo dal batch startBatch (1-4) */
  const runBatches = async (startBatch) => {
    setGenerating(true);
    const API_BATCH = `${API}/lead-sequence/generate-batch`;
    try {
      for (let b = startBatch; b <= 4; b++) {
        setGenerateStep(b);
        const r = await axios.post(`${API_BATCH}?batch=${b}`, {}, { headers, timeout: 55000 });
        setSequence(r.data.sequence);
        if (b < 4) {
          toast.success(`${BATCH_LABELS[b - 1]} generate (${r.data.total_so_far}/20 email)...`);
        }
      }
      toast.success('Completato! 20 email generate con AI su 120 giorni.');
      await loadAll();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || '';
      if (status === 502 || (detail && detail.toLowerCase().includes('budget'))) {
        toast.error(
          'Budget Universal Key esaurito. Ricarica da Profilo → Universal Key → Aggiungi saldo, poi clicca "Riprendi".',
          { duration: 8000 }
        );
      } else {
        toast.error(detail || 'Errore nella generazione AI. Clicca "Riprendi" per continuare.');
      }
      await loadAll(); // aggiorna lo stato per mostrare il pulsante Riprendi
    } finally {
      setGenerating(false);
      setGenerateStep(0);
    }
  };

  /** Genera da zero — elimina la sequenza esistente (batch 1) */
  const generateFromScratch = () => runBatches(1);

  /** Riprende dal batch successivo all'ultimo completato */
  const resumeGeneration = () => {
    const nextBatch = getNextBatch(sequence);
    if (nextBatch) runBatches(nextBatch);
  };

  const toggleActive = async () => {
    if (!sequence) return;
    setToggling(true);
    try {
      const { data } = await axios.put(`${API}/lead-sequence`,
        { ...sequence, active: !sequence.active }, { headers });
      setSequence(data.sequence);
      toast.success(data.sequence.active
        ? 'Sequenza attivata! I nuovi lead saranno iscritti automaticamente.'
        : 'Sequenza disattivata.');
    } catch {
      toast.error('Errore nel cambio stato.');
    } finally {
      setToggling(false);
    }
  };

  const startEditEmail = (idx) => {
    const email = sequence.emails[idx];
    setEditingEmail(idx);
    setEditSubject(email.subject);
    setEditBody(email.body_html);
    setExpandedEmail(idx);
  };

  const saveEmailEdit = async () => {
    const updatedEmails = sequence.emails.map((e, i) =>
      i === editingEmail ? { ...e, subject: editSubject, body_html: editBody } : e
    );
    try {
      const { data } = await axios.put(`${API}/lead-sequence`,
        { ...sequence, emails: updatedEmails }, { headers });
      setSequence(data.sequence);
      setEditingEmail(null);
      toast.success('Email aggiornata.');
    } catch {
      toast.error('Errore nel salvataggio.');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20" style={{ color: 'var(--lux-text-muted)' }}>
        <div className="w-6 h-6 border-2 rounded-full animate-spin mx-auto mb-3"
          style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
        <p className="text-sm">Caricamento sequenza...</p>
      </div>
    );
  }

  /* ── Stato parziale: alcuni batch completati ma non tutti ── */
  const nextBatch = getNextBatch(sequence);
  const isIncomplete = sequence && nextBatch !== null && (sequence.emails?.length || 0) < 20;
  const emailCount = sequence?.emails?.length || 0;

  return (
    <div data-testid="lead-sequence-tab">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="font-serif text-2xl font-light mb-1" style={{ color: 'var(--lux-text)' }}>
            Sequenza Drip Lead
          </h3>
          <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>
            {sequence
              ? `${emailCount}/20 email · ${sequence.active ? 'Attiva' : 'Inattiva'} · ${progress?.stats?.total_enrolled || 0} lead iscritti`
              : 'Nessuna sequenza. Usa AI per creare la tua prima campagna drip.'}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <button onClick={loadAll} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm"
            style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
            <RefreshCw size={12} /> Aggiorna
          </button>
          {sequence && (
            <button onClick={toggleActive} disabled={toggling}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm"
              style={{
                background: sequence.active ? 'rgba(220,38,38,0.1)' : 'rgba(5,150,105,0.1)',
                color: sequence.active ? '#dc2626' : '#059669',
                border: `1px solid ${sequence.active ? 'rgba(220,38,38,0.3)' : 'rgba(5,150,105,0.3)'}`,
              }}
              data-testid="sequence-toggle-btn">
              {toggling
                ? <Loader2 size={13} className="animate-spin" />
                : sequence.active ? <><Pause size={13} /> Disattiva</> : <><Play size={13} /> Attiva</>}
            </button>
          )}

          {/* Pulsante Riprendi (solo se generazione incompleta) */}
          {isIncomplete && !generating && (
            <button onClick={resumeGeneration}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-sm"
              style={{ background: 'rgba(201,168,76,0.12)', color: 'var(--lux-gold)', border: '1px solid rgba(201,168,76,0.4)' }}
              data-testid="resume-sequence-btn">
              <ArrowRight size={14} /> Riprendi ({emailCount}/20)
            </button>
          )}

          {/* Pulsante Genera / Rigenera */}
          <button
            onClick={isIncomplete ? generateFromScratch : generateFromScratch}
            disabled={generating}
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-sm"
            style={{ background: isIncomplete ? 'transparent' : 'var(--lux-gold)', color: isIncomplete ? 'var(--lux-text-muted)' : '#000', border: isIncomplete ? '1px solid var(--lux-border-2)' : 'none' }}
            data-testid="generate-sequence-btn">
            {generating
              ? <><Loader2 size={14} className="animate-spin" /> {generateStep > 0 ? `${BATCH_LABELS[generateStep - 1]}...` : 'Generazione...'}</>
              : isIncomplete
                ? <><RotateCcw size={13} /> Rigenera tutto</>
                : <><Zap size={14} /> {sequence ? 'Rigenera con AI' : 'Genera 20 email con AI'}</>}
          </button>
        </div>
      </div>

      {/* Banner generazione incompleta */}
      {isIncomplete && !generating && (
        <div data-testid="incomplete-banner" className="mb-6 flex items-start gap-3 p-4 rounded-sm"
          style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <AlertTriangle size={16} style={{ color: 'var(--lux-gold)', flexShrink: 0, marginTop: 1 }} />
          <div className="flex-1">
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--lux-text)' }}>
              Generazione incompleta — {emailCount}/20 email
            </p>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>
              La generazione AI si è interrotta (probabilmente per budget Universal Key esaurito).
              Ricarica da <strong style={{ color: 'var(--lux-gold)' }}>Profilo → Universal Key → Aggiungi saldo</strong>, poi clicca <strong>Riprendi</strong> per completare le {20 - emailCount} email mancanti.
            </p>
          </div>
          <button onClick={resumeGeneration}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-sm shrink-0"
            style={{ background: 'var(--lux-gold)', color: '#000' }}
            data-testid="resume-banner-btn">
            <ArrowRight size={12} /> Riprendi
          </button>
        </div>
      )}

      {/* Progress indicator during generation */}
      {generating && (
        <div className="mb-6">
          <div className="p-8 rounded-sm text-center mb-4"
            style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }}>
            <div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-4"
              style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
            <p className="font-serif text-lg font-light mb-2" style={{ color: 'var(--lux-text)' }}>
              {generateStep > 0
                ? `Batch ${generateStep}/4 — Claude scrive le ${BATCH_LABELS[generateStep - 1]}...`
                : 'Preparazione...'}
            </p>
            <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>
              20 email su 120 giorni. Circa 30s per batch.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className="flex items-center gap-2 flex-1 px-3 py-2.5 rounded-sm"
                style={{
                  background: generateStep > s ? 'rgba(5,150,105,0.08)' : generateStep === s ? 'rgba(201,168,76,0.08)' : 'var(--lux-surface-3)',
                  border: `1px solid ${generateStep > s ? 'rgba(5,150,105,0.3)' : generateStep === s ? 'rgba(201,168,76,0.3)' : 'var(--lux-border-2)'}`,
                }}>
                {generateStep > s
                  ? <Check size={12} style={{ color: '#059669', flexShrink: 0 }} />
                  : generateStep === s
                    ? <Loader2 size={12} className="animate-spin" style={{ color: 'var(--lux-gold)', flexShrink: 0 }} />
                    : <div className="w-3 h-3 rounded-full shrink-0" style={{ background: 'var(--lux-border-2)' }} />}
                <span className="text-[10px]" style={{ color: generateStep > s ? '#059669' : generateStep === s ? 'var(--lux-gold)' : 'var(--lux-text-muted)' }}>
                  {BATCH_LABELS[s - 1]}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!sequence && !generating && (
        <div className="py-20 text-center rounded-sm" style={{ border: '1px dashed var(--lux-border-2)' }}>
          <div className="w-14 h-14 rounded-sm flex items-center justify-center mx-auto mb-5"
            style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
            <Zap size={24} style={{ color: 'var(--lux-gold)' }} />
          </div>
          <h4 className="font-serif text-xl font-light mb-3" style={{ color: 'var(--lux-text)' }}>
            Nessuna sequenza creata
          </h4>
          <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--lux-text-muted)' }}>
            Claude scriverà <strong style={{ color: 'var(--lux-gold)' }}>20 email</strong> su 120 giorni per convertire i tuoi lead in clienti paganti.
          </p>
          <button onClick={generateFromScratch} disabled={generating}
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold"
            style={{ background: 'var(--lux-gold)', color: '#000' }}>
            <Zap size={15} /> Genera sequenza con AI
          </button>
        </div>
      )}

      {/* Sequence timeline */}
      {sequence && !generating && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Timeline */}
          <div className="lg:col-span-2">
            <div className="space-y-3">
              {(sequence.emails || []).map((email, idx) => {
                const stepInfo = STEP_LABELS[email.order] || { label: `Step ${email.order}`, color: '#888' };
                const isExpanded = expandedEmail === idx;
                const isEditing = editingEmail === idx;

                return (
                  <div key={idx} className="rounded-sm overflow-hidden"
                    style={{ border: `1px solid ${isExpanded ? stepInfo.color + '40' : 'var(--lux-border-1)'}` }}>
                    {/* Header */}
                    <div className="flex items-center gap-4 px-5 py-4 cursor-pointer"
                      style={{ background: isExpanded ? `${stepInfo.color}08` : 'var(--lux-surface-1)' }}
                      onClick={() => setExpandedEmail(isExpanded ? null : idx)}>
                      <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                        style={{ background: `${stepInfo.color}18`, border: `1px solid ${stepInfo.color}30` }}>
                        <span className="text-xs font-bold" style={{ color: stepInfo.color }}>
                          {String(email.order).padStart(2, '0')}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] uppercase tracking-widest font-medium"
                            style={{ color: stepInfo.color }}>{stepInfo.label}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full"
                            style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted-3)' }}>
                            <Clock size={9} className="inline mr-1" />
                            Giorno {email.delay_days}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate" style={{ color: 'var(--lux-text)' }}>
                          {email.subject}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={e => { e.stopPropagation(); startEditEmail(idx); }}
                          className="p-1.5 rounded"
                          style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)' }}
                          data-testid={`edit-email-${idx}`}>
                          <Edit3 size={13} />
                        </button>
                        {isExpanded
                          ? <ChevronUp size={16} style={{ color: 'var(--lux-text-muted)' }} />
                          : <ChevronDown size={16} style={{ color: 'var(--lux-text-muted)' }} />}
                      </div>
                    </div>

                    {/* Expanded: edit or preview */}
                    {isExpanded && (
                      <div className="px-5 pb-5 pt-1"
                        style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)' }}>
                        {isEditing ? (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-[10px] uppercase tracking-widest mb-1.5"
                                style={{ color: 'var(--lux-text-muted-2)' }}>Oggetto</label>
                              <input value={editSubject} onChange={e => setEditSubject(e.target.value)}
                                className="w-full px-3 py-2.5 text-sm rounded-sm"
                                style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-widest mb-1.5"
                                style={{ color: 'var(--lux-text-muted-2)' }}>Corpo (HTML)</label>
                              <textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={8}
                                className="w-full px-3 py-2.5 text-sm rounded-sm font-mono resize-y"
                                style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={saveEmailEdit} className="px-4 py-2 text-xs font-medium rounded-sm"
                                style={{ background: 'var(--lux-gold)', color: '#000' }}>
                                <Check size={12} className="inline mr-1" /> Salva
                              </button>
                              <button onClick={() => setEditingEmail(null)} className="px-4 py-2 text-xs rounded-sm"
                                style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
                                Annulla
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-xs mb-2" style={{ color: 'var(--lux-text-muted)' }}>
                              Variabili: {'{{nome}}'} {'{{struttura}}'} {'{{piano}}'}
                            </p>
                            <div className="p-3 rounded-sm text-xs overflow-auto max-h-48 font-mono"
                              style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)', border: '1px solid var(--lux-border-2)' }}>
                              {email.body_html?.substring(0, 400)}
                              {email.body_html?.length > 400 && '...'}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats sidebar */}
          <div className="space-y-4">
            {/* Status card */}
            <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--lux-gold)' }}>Stato sequenza</p>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2.5 h-2.5 rounded-full"
                  style={{ background: sequence.active ? '#059669' : '#6b7280' }} />
                <span className="text-sm font-medium"
                  style={{ color: sequence.active ? '#059669' : 'var(--lux-text-muted)' }}>
                  {sequence.active ? 'Attiva' : 'Inattiva'}
                </span>
              </div>
              {!sequence.active && (
                <div className="flex items-start gap-2 p-3 rounded-sm"
                  style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
                  <AlertTriangle size={13} style={{ color: 'var(--lux-gold)', flexShrink: 0, marginTop: 1 }} />
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>
                    Attiva la sequenza per iniziare ad iscrivere automaticamente i nuovi lead.
                  </p>
                </div>
              )}
              {sequence.generated_at && (
                <p className="text-xs mt-3" style={{ color: 'var(--lux-text-muted-3)' }}>
                  Generata il {new Date(sequence.generated_at).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>

            {/* Progress stats */}
            {progress && (
              <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
                <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--lux-gold)' }}>Statistiche invii</p>
                <div className="space-y-3">
                  {[
                    { label: 'Lead iscritti', value: progress.stats?.total_enrolled || 0, icon: Users },
                    { label: 'In corso', value: progress.stats?.in_progress || 0, icon: Mail },
                    { label: 'Completati', value: progress.stats?.completed || 0, icon: Check },
                  ].map(({ label, value, icon: Icon }) => (
                    <div key={label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon size={13} style={{ color: 'var(--lux-text-muted)' }} />
                        <span className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>{label}</span>
                      </div>
                      <span className="font-medium text-sm" style={{ color: 'var(--lux-text)' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How it works */}
            <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--lux-gold)' }}>Come funziona</p>
              <div className="space-y-2.5 text-xs" style={{ color: 'var(--lux-text-muted)' }}>
                <p>1. AI scrive la sequenza una volta</p>
                <p>2. Ogni nuovo lead viene iscritto automaticamente</p>
                <p>3. Il cron orario invia le email al momento giusto</p>
                <p>4. Il lead completa i 20 step in 120 giorni</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
